'use client';

import { Tab } from '@common/components';
import { useSearchParams } from 'next/navigation';
import {
  type CSSProperties,
  Suspense,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/_components/DonationLayout/DonationSubHeader';
import { useScrollToSection } from '@/app/donation/_hooks/useScrollToSection';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { TABS, TABS_LABELS } from '../constants';
import type { Tab as TabType } from '../types';

interface GiftDetailTabProps {
  className?: string;
  style?: CSSProperties;
  productNumber: string;
  disabledTabs?: TabType[];
}

const MOBILE_STICKY_OFFSET = 48;
const DESKTOP_STICKY_OFFSET = DONATION_DESKTOP_STICKY_TOP;

const GiftDetailTabComponent = ({
  className,
  style,
  disabledTabs,
}: GiftDetailTabProps) => {
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const { scrollToSectionWithTab } = useScrollToSection();
  const searchParams = useSearchParams();

  const [tab, setTab] = useState<TabType>(TABS.BASIC);
  const isUserScrolling = useRef(true);

  // 초기 마운트 시 searchParams의 tab이 있으면 해당 섹션으로 스크롤
  useEffect(() => {
    if (searchParams?.get('tab')) {
      scrollToSectionWithTab(searchParams.get('tab') as TabType, {
        behavior: 'instant',
        offset: isDesktopViewport
          ? DESKTOP_STICKY_OFFSET
          : MOBILE_STICKY_OFFSET,
      });
    }
  }, [isDesktopViewport, scrollToSectionWithTab, searchParams]);

  // IntersectionObserver로 활성 탭 감지 (reflow 없음)
  useEffect(() => {
    const tabIds = Object.values(TABS).filter(
      (id) => !disabledTabs?.includes(id),
    );

    const observer = new IntersectionObserver(
      (entries) => {
        if (!isUserScrolling.current) return;

        for (const entry of entries) {
          if (entry.isIntersecting) {
            const activeId = tabIds.find((id) => id === entry.target.id);
            if (activeId) {
              setTab(activeId);
            }
          }
        }
      },
      {
        // 상단 0px, 하단 50% 영역에서 교차 감지
        rootMargin: '0px 0px -50% 0px',
        threshold: 0,
      },
    );

    // 각 섹션 관찰
    tabIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [disabledTabs]);

  return (
    <Tab
      size={isDesktopViewport ? 'lg' : 'sm'}
      className={className}
      style={style}
      variant="full"
      value={tab ?? TABS.BASIC}
      onChange={(value) => {
        scrollToSectionWithTab(value, {
          offset: isDesktopViewport
            ? DESKTOP_STICKY_OFFSET
            : MOBILE_STICKY_OFFSET,
        });
      }}
    >
      {Object.values(TABS)
        .filter((tab) => !disabledTabs?.includes(tab))
        .map((tab) => (
          <Tab.Item key={tab} value={tab}>
            {TABS_LABELS[tab]}
          </Tab.Item>
        ))}
    </Tab>
  );
};

export const GiftDetailTab = (props: GiftDetailTabProps) => {
  return (
    <ErrorBoundary fallback={null}>
      <Suspense>
        <GiftDetailTabComponent {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};
